<script setup>
import { toRef } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import { useReportLoading } from '../composables/useReportLoading';

const props = defineProps({
  compact: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  loadingMessage: {
    type: String,
    default: '',
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select']);

const select = () => {
  if (props.selectable) emit('select');
};

const visibleLoading = useReportLoading(toRef(props, 'isLoading'));
</script>

<template>
  <section
    :aria-busy="visibleLoading || undefined"
    :aria-pressed="selectable ? selected : undefined"
    :role="selectable ? 'button' : undefined"
    :tabindex="selectable ? 0 : undefined"
    class="relative overflow-hidden border border-solid rounded-md bg-white dark:bg-slate-800 border-slate-75 dark:border-slate-700"
    :class="[
      compact ? 'p-4' : 'p-5',
      selectable &&
        'cursor-pointer transition-colors hover:border-woot-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-woot-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900',
      selected &&
        'border-woot-500 bg-woot-50/40 ring-1 ring-woot-500 dark:bg-woot-900/20',
    ]"
    @click="select"
    @keydown.enter.prevent="select"
    @keydown.space.prevent="select"
  >
    <transition name="fade">
      <div
        v-if="visibleLoading"
        class="absolute inset-0 z-10 flex items-center justify-center gap-2 text-sm bg-white/70 dark:bg-slate-800/70 text-slate-700 dark:text-slate-100"
      >
        <Spinner size="small" />
        <span v-if="loadingMessage">{{ loadingMessage }}</span>
      </div>
    </transition>
    <slot />
  </section>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
